import { MapPin, Plus, Store, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createNurturing, searchCompanies, getNurturing, updateNurturing } from "../../interfaces/nurturings";
import { INurturing, NurturingType } from "../../interfaces/nurturing";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";

const NURTURING_TYPE_LABELS: Record<NurturingType, string> = {
    owner_tracking: "Suivi propriétaire",
    group_tracking: "Suivi d'un groupe",
    franchise_tracking: "Suivi franchise",
    chef_tracking: "Suivi chef de cuisine",
    multisite_tracking: "Suivi multi-sites",
};

const PRIORITY_LABELS: Record<'low' | 'medium' | 'high', string> = {
    low: "Faible",
    medium: "Moyenne",
    high: "Haute",
};

function urlFieldClass(hasError?: boolean) {
    return `h-11 w-full rounded-lg border bg-surface px-3 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:border-accent ${
        hasError ? "border-danger focus-visible:ring-danger" : "border-border focus-visible:ring-accent"
    }`;
}

export default function CreateNurturingForm() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [searchQuery, setSearchQuery] = useState("");
    const [companySuggestions, setCompanySuggestions] = useState<any[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [formData, setFormData] = useState<Partial<INurturing>>({
        entityName: "",
        displayName: "",
        type: "owner_tracking",
        status: "active",
        googleMapsUrl: "",
        linkedinUrl: "",
        websiteUrl: "",
        instagramUrl: "",
        tiktokUrl: "",
        phone: "",
        email: "",
        siren: "",
        siret: "",
        notes: "",
        tags: [],
        priority: "medium",
        events: [],
    });

    const [tagInput, setTagInput] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Load existing nurturing if in edit mode
    useEffect(() => {
        if (isEditMode && id) {
            loadNurturing();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEditMode]);

    const loadNurturing = async () => {
        try {
            setInitialLoading(true);
            const data = await getNurturing(id!);
            setFormData({
                entityName: data.entityName,
                displayName: data.displayName || "",
                type: data.type,
                status: data.status,
                googleMapsUrl: data.googleMapsUrl || "",
                linkedinUrl: data.linkedinUrl || "",
                websiteUrl: data.websiteUrl || "",
                instagramUrl: data.instagramUrl || "",
                tiktokUrl: data.tiktokUrl || "",
                phone: data.phone || "",
                email: data.email || "",
                siren: data.siren || "",
                siret: data.siret || "",
                notes: data.notes || "",
                tags: data.tags || [],
                priority: data.priority || "medium",
                company: data.company,
            });
            setSearchQuery(data.entityName);
        } catch (error) {
            console.error("Failed to load nurturing:", error);
            alert("Échec du chargement du nurturing");
            navigate("/nurturings");
        } finally {
            setInitialLoading(false);
        }
    };

    // Auto-suggest companies
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (searchQuery.length >= 2) {
                handleSearchCompanies(searchQuery);
            } else {
                setCompanySuggestions([]);
                setShowSuggestions(false);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleSearchCompanies = async (query: string) => {
        try {
            const result = await searchCompanies(query);
            setCompanySuggestions(result.data || []);
            setShowSuggestions(true);
        } catch (error) {
            console.error("Failed to search companies:", error);
            setCompanySuggestions([]);
        }
    };

    const handleSelectCompany = (company: any) => {
        setFormData(prev => ({
            ...prev,
            entityName: company.name,
            displayName: company.displayName || "",
            websiteUrl: company.website || "",
            siren: company.siren || "",
            company: company._id,
        }));
        setSearchQuery(company.name);
        setShowSuggestions(false);
    };

    const handleInputChange = (field: keyof INurturing, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: "" }));
        }

        if (field === "entityName") {
            setSearchQuery(value);
        }
    };

    const addTag = () => {
        if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...(prev.tags || []), tagInput.trim()]
            }));
            setTagInput("");
        }
    };

    const removeTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: (prev.tags || []).filter(tag => tag !== tagToRemove)
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.entityName || formData.entityName.trim().length < 2) {
            newErrors.entityName = "Le nom de l'établissement est requis (min 2 caractères)";
        }

        if (!formData.type) {
            newErrors.type = "Le type de nurturing est requis";
        }

        if (formData.googleMapsUrl && !isValidUrl(formData.googleMapsUrl)) {
            newErrors.googleMapsUrl = "URL Google Maps invalide";
        }

        if (formData.linkedinUrl && !isValidUrl(formData.linkedinUrl)) {
            newErrors.linkedinUrl = "URL LinkedIn invalide";
        }

        if (formData.websiteUrl && !isValidUrl(formData.websiteUrl)) {
            newErrors.websiteUrl = "URL du site web invalide";
        }

        if (formData.instagramUrl && !isValidUrl(formData.instagramUrl)) {
            newErrors.instagramUrl = "URL Instagram invalide";
        }

        if (formData.tiktokUrl && !isValidUrl(formData.tiktokUrl)) {
            newErrors.tiktokUrl = "URL TikTok invalide";
        }

        if (formData.email && !isValidEmail(formData.email)) {
            newErrors.email = "Email invalide";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const isValidEmail = (email: string): boolean => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            if (isEditMode && id) {
                const updated = await updateNurturing(id, formData);
                navigate(`/nurturings/${updated._id}`);
            } else {
                const created = await createNurturing(formData);
                navigate(`/nurturings/${created._id}`);
            }
        } catch (error: any) {
            console.error(`Failed to ${isEditMode ? 'update' : 'create'} nurturing:`, error);
            alert(error.response?.data?.error || `Échec de ${isEditMode ? 'la mise à jour' : 'la création'} du nurturing`);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-sm text-ink-muted">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-4xl px-4 md:px-8 py-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">
                        {isEditMode ? "Modifier le nurturing" : "Nouveau nurturing"}
                    </h1>
                    <p className="text-sm text-ink-muted mt-1">
                        Configurez le suivi automatisé d'un établissement
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Entity Name with Auto-suggest */}
                    <div className="rounded-2xl border border-border bg-surface p-6">
                        <h2 className="font-sans text-sm font-semibold text-ink mb-4">
                            Informations de base
                        </h2>

                        <div className="space-y-4">
                            <div className="relative">
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Nom du restaurant <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleInputChange("entityName", e.target.value)}
                                    placeholder="Rechercher ou saisir..."
                                    className={urlFieldClass(!!errors.entityName)}
                                    required
                                />
                                {errors.entityName && (
                                    <p className="text-xs text-danger mt-1">{errors.entityName}</p>
                                )}

                                {/* Auto-suggestions */}
                                {showSuggestions && companySuggestions.length > 0 && (
                                    <div className="absolute z-10 w-full mt-1 rounded-2xl border border-border bg-surface shadow-float max-h-60 overflow-y-auto p-2">
                                        {companySuggestions.map((company) => (
                                            <button
                                                key={company._id}
                                                type="button"
                                                onClick={() => handleSelectCompany(company)}
                                                className="w-full px-3 py-2 text-left rounded-lg hover:bg-surface-sunken transition-colors duration-150"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Store className="h-5 w-5 text-ink-faint" />
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm text-ink">{company.name}</div>
                                                        {company.location && (
                                                            <div className="text-xs text-ink-muted flex items-center gap-1 mt-0.5">
                                                                <MapPin className="h-3 w-3" />
                                                                {company.location}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {company.siren && (
                                                        <span className="text-xs text-ink-faint font-mono">
                                                            {company.siren}
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Nom d'affichage (optionnel)
                                </label>
                                <Input
                                    type="text"
                                    value={formData.displayName || ""}
                                    onChange={(e) => handleInputChange("displayName", e.target.value)}
                                    placeholder="Ex: Chez Marcel - Paris 11e"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Type de nurturing <span className="text-danger">*</span>
                                </label>
                                <Select
                                    fullWidth
                                    value={formData.type}
                                    onChange={(v) => handleInputChange("type", v as NurturingType)}
                                    options={Object.entries(NURTURING_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Priorité
                                </label>
                                <Select
                                    fullWidth
                                    value={formData.priority}
                                    onChange={(v) => handleInputChange("priority", v)}
                                    options={Object.entries(PRIORITY_LABELS).map(([value, label]) => ({ value, label }))}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Links */}
                    <div className="rounded-2xl border border-border bg-surface p-6">
                        <h2 className="font-sans text-sm font-semibold text-ink mb-4">Liens et contacts</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Google Maps URL
                                </label>
                                <input
                                    type="url"
                                    value={formData.googleMapsUrl || ""}
                                    onChange={(e) => handleInputChange("googleMapsUrl", e.target.value)}
                                    placeholder="https://maps.google.com/..."
                                    className={urlFieldClass(!!errors.googleMapsUrl)}
                                />
                                {errors.googleMapsUrl && (
                                    <p className="text-xs text-danger mt-1">{errors.googleMapsUrl}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    LinkedIn URL
                                </label>
                                <input
                                    type="url"
                                    value={formData.linkedinUrl || ""}
                                    onChange={(e) => handleInputChange("linkedinUrl", e.target.value)}
                                    placeholder="https://linkedin.com/..."
                                    className={urlFieldClass(!!errors.linkedinUrl)}
                                />
                                {errors.linkedinUrl && (
                                    <p className="text-xs text-danger mt-1">{errors.linkedinUrl}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Site web
                                </label>
                                <input
                                    type="url"
                                    value={formData.websiteUrl || ""}
                                    onChange={(e) => handleInputChange("websiteUrl", e.target.value)}
                                    placeholder="https://..."
                                    className={urlFieldClass(!!errors.websiteUrl)}
                                />
                                {errors.websiteUrl && (
                                    <p className="text-xs text-danger mt-1">{errors.websiteUrl}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-ink mb-1.5">
                                        Instagram URL
                                    </label>
                                    <input
                                        type="url"
                                        value={formData.instagramUrl || ""}
                                        onChange={(e) => handleInputChange("instagramUrl", e.target.value)}
                                        placeholder="https://instagram.com/..."
                                        className={urlFieldClass(!!errors.instagramUrl)}
                                    />
                                    {errors.instagramUrl && (
                                        <p className="text-xs text-danger mt-1">{errors.instagramUrl}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-ink mb-1.5">
                                        TikTok URL
                                    </label>
                                    <input
                                        type="url"
                                        value={formData.tiktokUrl || ""}
                                        onChange={(e) => handleInputChange("tiktokUrl", e.target.value)}
                                        placeholder="https://tiktok.com/..."
                                        className={urlFieldClass(!!errors.tiktokUrl)}
                                    />
                                    {errors.tiktokUrl && (
                                        <p className="text-xs text-danger mt-1">{errors.tiktokUrl}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-ink mb-1.5">
                                        Téléphone
                                    </label>
                                    <Input
                                        type="tel"
                                        value={formData.phone || ""}
                                        onChange={(e) => handleInputChange("phone", e.target.value)}
                                        placeholder="+33 1 23 45 67 89"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-ink mb-1.5">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email || ""}
                                        onChange={(e) => handleInputChange("email", e.target.value)}
                                        placeholder="contact@restaurant.fr"
                                        className={urlFieldClass(!!errors.email)}
                                    />
                                    {errors.email && (
                                        <p className="text-xs text-danger mt-1">{errors.email}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-ink mb-1.5">
                                        SIREN
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.siren || ""}
                                        onChange={(e) => handleInputChange("siren", e.target.value)}
                                        placeholder="123456789"
                                        maxLength={9}
                                        className="font-mono"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-ink mb-1.5">
                                        SIRET
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.siret || ""}
                                        onChange={(e) => handleInputChange("siret", e.target.value)}
                                        placeholder="12345678900123"
                                        maxLength={14}
                                        className="font-mono"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Notes and Tags */}
                    <div className="rounded-2xl border border-border bg-surface p-6">
                        <h2 className="font-sans text-sm font-semibold text-ink mb-4">Notes et tags</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Notes internes
                                </label>
                                <textarea
                                    value={formData.notes || ""}
                                    onChange={(e) => handleInputChange("notes", e.target.value)}
                                    placeholder="Informations supplémentaires..."
                                    rows={4}
                                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-sans text-sm text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Tags
                                </label>
                                <div className="flex gap-2 mb-2">
                                    <Input
                                        type="text"
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addTag();
                                            }
                                        }}
                                        placeholder="Ajouter un tag"
                                        className="flex-1"
                                    />
                                    <Button type="button" onClick={addTag}>
                                        <Plus className="h-5 w-5" />
                                    </Button>
                                </div>
                                {formData.tags && formData.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.tags.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-surface-sunken text-ink-muted rounded-full text-sm"
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(tag)}
                                                    className="hover:text-danger transition-colors duration-150"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-6 border-t border-border">
                        <Button type="button" variant="secondary" onClick={() => navigate("/nurturings")}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading
                                ? (isEditMode ? "Mise à jour..." : "Création...")
                                : (isEditMode ? "Mettre à jour" : "Créer le nurturing")
                            }
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
